import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { CUSTOMER_DESIGN_MOCKUPS_COLLECTION } from './designMockupIndex';
import { normalizeDesignName, scoreDesignNameSimilarity } from './designNameMatching';
import { LINE_TYPES, SIZES } from '../firebase/schema';

const libraryCache = new Map();
const inflightRequests = new Map();

export function invalidateCustomerDesignLibrary(customerId) {
  if (customerId) {
    libraryCache.delete(customerId);
    inflightRequests.delete(customerId);
  } else {
    libraryCache.clear();
    inflightRequests.clear();
  }
}

/**
 * Load all design library entries for a customer (cached in memory).
 */
export async function fetchCustomerDesignLibrary(customerId, { forceRefresh = false } = {}) {
  if (!customerId) return [];

  if (!forceRefresh && libraryCache.has(customerId)) {
    return libraryCache.get(customerId);
  }

  if (!forceRefresh && inflightRequests.has(customerId)) {
    return inflightRequests.get(customerId);
  }

  const request = (async () => {
    const snap = await getDocs(
      query(
        collection(db, CUSTOMER_DESIGN_MOCKUPS_COLLECTION),
        where('customerId', '==', customerId)
      )
    );

    const entries = snap.docs
      .map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }))
      .filter((entry) => entry.displayName && entry.mockupUrl)
      .sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() ?? a.updatedAtMs ?? 0;
        const bTime = b.updatedAt?.toMillis?.() ?? b.updatedAtMs ?? 0;
        return bTime - aTime;
      });

    libraryCache.set(customerId, entries);
    inflightRequests.delete(customerId);
    return entries;
  })();

  inflightRequests.set(customerId, request);
  return request;
}

/**
 * Fast local filter for design autocomplete (no network).
 */
export function filterCustomerDesignLibrary(library, searchText, limit = 8) {
  const trimmed = (searchText || '').trim();
  if (!trimmed || trimmed.length < 2 || !library?.length) return [];

  const normalizedQuery = normalizeDesignName(trimmed);

  return library
    .map((entry) => {
      const normalizedName = normalizeDesignName(entry.displayName);
      const prefixMatch = normalizedName.includes(normalizedQuery);
      const score = scoreDesignNameSimilarity(trimmed, entry.displayName);
      return {
        entry,
        score: prefixMatch ? Math.max(score, 0.8) : score
      };
    })
    .filter(({ score }) => score >= 0.4)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ entry }) => entry);
}

export function mapDbTypeToLineType(type) {
  if (!type) return LINE_TYPES.SCREEN_PRINT;
  const t = String(type).toLowerCase();
  if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
  if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
  if (t === 'dtg') return LINE_TYPES.DTG;
  if (t === 'dtf') return LINE_TYPES.DTF;
  if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
  if (Object.values(LINE_TYPES).includes(type)) return type;
  return LINE_TYPES.SCREEN_PRINT;
}

function normalizeSizeType(sizeType) {
  if (!sizeType) return 'ADULT';
  if (SIZES[sizeType]) return sizeType;
  const normalized = String(sizeType).toUpperCase();
  if (SIZES[normalized]) return normalized;
  return 'ADULT';
}

function hasGarmentSnapshot(snap) {
  return !!(snap?.garmentId?.trim() || snap?.garmentColor?.trim());
}

export function inferPrintTypeFromSnapshot(snap, fallbackType) {
  const mapped = mapDbTypeToLineType(snap?.type || fallbackType);
  if (hasGarmentSnapshot(snap) && mapped === LINE_TYPES.DESIGN_ONLY) {
    return LINE_TYPES.SCREEN_PRINT;
  }
  if (!snap?.type && hasGarmentSnapshot(snap)) {
    return LINE_TYPES.SCREEN_PRINT;
  }
  return mapped;
}

/**
 * Snapshot of line fields to restore on repeat designs (no size quantities).
 */
export function buildLineSnapshotFromLine(line) {
  if (!line) return null;

  const production = Array.isArray(line.files?.production)
    ? line.files.production
        .filter((file) => file?.url && !String(file.url).startsWith('blob:'))
        .map((file) => ({
          name: file.name,
          url: file.url,
          type: file.type || 'unassigned'
        }))
    : [];

  return {
    garmentId: line.garmentId || line.style || '',
    garmentColor: line.garmentColor || line.color || '',
    type: line.type || 'screen',
    frontInkColors: Array.isArray(line.frontInkColors) ? [...line.frontInkColors] : [],
    backInkColors: Array.isArray(line.backInkColors) ? [...line.backInkColors] : [],
    otherInkColors: Array.isArray(line.otherInkColors) ? [...line.otherInkColors] : [],
    printLocations: Array.isArray(line.printLocations) ? [...line.printLocations] : [],
    pressSetup: line.pressSetup ? { ...line.pressSetup } : null,
    pricePerPiece: line.pricePerPiece ?? line.price ?? null,
    cogs: line.cogs ?? null,
    sizeType: line.sizeType || 'ADULT',
    inkNotes: line.inkNotes || '',
    productionFiles: production
  };
}

/**
 * Apply a library entry onto a line's form state.
 */
export function applyDesignTemplateToLine(lineData, entry) {
  if (!entry) return lineData;

  const snap = entry.lineSnapshot || {};
  const mockupUrl = entry.mockupUrl || null;
  const printType = inferPrintTypeFromSnapshot(snap, lineData.type);
  const sizeType = normalizeSizeType(snap.sizeType || lineData.sizeType);

  const next = {
    ...lineData,
    design: entry.displayName,
    type: printType,
    isExpanded: true
  };

  if (hasGarmentSnapshot(snap)) {
    if (snap.garmentId) next.style = snap.garmentId;
    if (snap.garmentColor) next.color = snap.garmentColor;
    next.sizeType = sizeType;
    next.sizes = Object.fromEntries(
      (SIZES[sizeType] || SIZES.ADULT).map((size) => [size, lineData.sizes?.[size] ?? ''])
    );
    next.frontInkColors = Array.isArray(snap.frontInkColors) ? [...snap.frontInkColors] : [];
    next.backInkColors = Array.isArray(snap.backInkColors) ? [...snap.backInkColors] : [];
    next.otherInkColors = Array.isArray(snap.otherInkColors) ? [...snap.otherInkColors] : [];
    next.printLocations = Array.isArray(snap.printLocations) ? [...snap.printLocations] : [];
  } else {
    if (snap.garmentId) next.style = snap.garmentId;
    if (snap.garmentColor) next.color = snap.garmentColor;
    if (snap.sizeType) next.sizeType = sizeType;
    if (snap.frontInkColors?.length) next.frontInkColors = snap.frontInkColors;
    if (snap.backInkColors?.length) next.backInkColors = snap.backInkColors;
    if (snap.otherInkColors?.length) next.otherInkColors = snap.otherInkColors;
    if (snap.printLocations?.length) next.printLocations = snap.printLocations;
  }

  if (snap.pressSetup) next.pressSetup = snap.pressSetup;
  if (snap.inkNotes) next.inkNotes = snap.inkNotes;
  if (snap.pricePerPiece != null && snap.pricePerPiece !== '') {
    next.price = snap.pricePerPiece;
    next.pricePerPiece = snap.pricePerPiece;
  }
  if (snap.cogs != null && snap.cogs !== '') next.cogs = snap.cogs;

  if (mockupUrl) {
    next.designFileUrl = mockupUrl;
    next.files = {
      ...(lineData.files || {}),
      mockup: {
        url: mockupUrl,
        fileName: entry.mockupFileName || null
      },
      ...(snap.productionFiles?.length
        ? { production: snap.productionFiles }
        : {})
    };
  } else if (snap.productionFiles?.length) {
    next.files = {
      ...(lineData.files || {}),
      production: snap.productionFiles
    };
  }

  return next;
}

/**
 * Attach an existing library mockup to a line without re-uploading.
 */
export function applyLibraryMockupToLine(lineData, entry) {
  if (!entry?.mockupUrl) return lineData;

  const hasDesignName = !!(lineData.design || lineData.designName || '').trim();

  return {
    ...lineData,
    ...(hasDesignName ? {} : { design: entry.displayName || lineData.design }),
    designFileUrl: entry.mockupUrl,
    files: {
      ...(lineData.files || {}),
      mockup: {
        url: entry.mockupUrl,
        fileName: entry.mockupFileName || null
      }
    }
  };
}

export function getDesignLibrarySubtitle(entry) {
  const snap = entry?.lineSnapshot;
  if (!snap) return entry?.sourceOrderNumber ? `PO ${entry.sourceOrderNumber}` : '';

  const parts = [
    snap.garmentId?.split(' - ')?.[0] || snap.garmentId,
    snap.garmentColor
  ].filter(Boolean);

  return parts.join(' · ');
}
